import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BrandedCard, BrandedInput, BrandedButton, BootPulse } from '../components';
import { kratosService, KratosFlow, nodeValue } from '../services/kratosService';
import { challengeIn, sameOriginPath, signInPath } from '../services/signInFlow';
import './Verification.css';

const hasNode = (flow: KratosFlow | null, name: string): boolean =>
  !!flow?.ui?.nodes?.some((node) => node.attributes?.name === name);

/** Kratos's error for a flow it answered 200 (a wrong code keeps the flow open). */
const flowError = (flow: KratosFlow): string | undefined =>
  flow.ui?.messages?.find((message) => message.type === 'error')?.text;

/**
 * /verification — Ory Kratos self-service email verification (code
 * strategy): email in, code back.
 *
 * Usually Kratos has already mailed the code — after sign-up, or when an
 * unconfirmed address tries to sign in — and sends the browser here with
 * that flow (?flow=; the emailed link also prefills the code). The page
 * adopts it, so the code from that email is the one that works. With a
 * login_challenge (in the URL, or in the flow's continue link) it goes on
 * to sign in and back to the application.
 */
export const Verification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const loginChallenge = searchParams.get('login_challenge');
  const arrival = (location.state ?? {}) as { email?: string };

  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const [email, setEmail] = useState(arrival.email ?? '');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [continueTo, setContinueTo] = useState('/login');

  // Verified: on to sign in. Where Kratos says to continue (the return_to the
  // sign-in page gave it) counts only when it is a sign-in on this site.
  const complete = (passed: KratosFlow, address: string) => {
    const continueLink = passed.ui?.nodes?.find((node) => node.attributes?.id === 'continue');
    const kratosNext = sameOriginPath(continueLink?.attributes.href ?? passed.ui?.action);
    const next = loginChallenge
      ? signInPath(loginChallenge)
      : kratosNext?.startsWith('/login')
        ? kratosNext
        : '/login';
    // An application is waiting: go straight on to finishing its sign-in.
    if (challengeIn(next)) {
      navigate(next, { state: { email: address, verified: true } });
      return;
    }
    setContinueTo(next);
    setDone(true);
    setInfo('Your email address has been verified.');
  };

  useEffect(() => {
    const init = async () => {
      const existing = searchParams.get('flow');
      try {
        let loaded: KratosFlow | null = null;
        if (existing) {
          try {
            loaded = await kratosService.getFlow('verification', existing);
          } catch {
            // Expired or already used: start over with a fresh code.
            setInfo(
              'That verification request has expired. Enter your email address to get a new code.'
            );
          }
        }
        loaded = loaded ?? (await kratosService.initVerificationFlow());
        const mailedTo = nodeValue(loaded, 'email') || arrival.email || '';
        setFlow(loaded);
        setEmail(mailedTo);
        if (loaded.state === 'passed_challenge') {
          complete(loaded, mailedTo);
          return;
        }
        if (hasNode(loaded, 'code')) {
          // The emailed link prefills the code; confirming is still a click,
          // so a mail scanner following links can't confirm by itself.
          const prefilled = nodeValue(loaded, 'code');
          setCode(prefilled);
          setInfo(
            prefilled
              ? 'Select Verify code to confirm your email address.'
              : `We emailed a verification code to ${mailedTo || 'your address'}. Enter it below.`
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to start email verification.');
      } finally {
        setLoading(false);
      }
    };

    init();
    // Runs once: the flow in the URL is the one this page completes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow) return;
    setError('');
    setSubmitting(true);

    try {
      const updated = await kratosService.submitVerification(flow.id, { method: 'code', email });
      setFlow(updated);
      setInfo('Check your email for a verification code.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send a verification code.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!flow || !email) return;
    setError('');
    setSubmitting(true);
    try {
      const updated = await kratosService.submitVerification(flow.id, { method: 'code', email });
      setFlow(updated);
      setCode('');
      setInfo(`We sent a new code to ${email}. Only the newest code works.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send a new code.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow) return;
    setError('');
    setSubmitting(true);

    try {
      const updated = await kratosService.submitVerification(flow.id, { method: 'code', code: code.trim() });
      if (updated.state === 'passed_challenge') {
        setFlow(updated);
        complete(updated, email);
        return;
      }
      // A wrong or used code: Kratos keeps the flow open and says why.
      if (updated.ui) setFlow(updated);
      setError(flowError(updated) ?? 'That code was not valid.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code was not valid.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <BrandedCard>
        <BootPulse label="Loading email verification" />
      </BrandedCard>
    );
  }

  const awaitingCode = !done && hasNode(flow, 'code');

  return (
    <BrandedCard title="Verify Your Email">
      {error && (
        <div className="verification-error" role="alert">
          {error}
        </div>
      )}
      {info && !error && (
        <div className="verification-info" role="status">
          {info}
        </div>
      )}

      {done ? (
        <div className="verification-links">
          <Link
            to={continueTo}
            state={{ email, verified: true }}
            className="verification-link"
          >
            Continue to sign in
          </Link>
        </div>
      ) : awaitingCode ? (
        <form onSubmit={handleSubmitCode} className="verification-form">
          <BrandedInput
            type="text"
            name="code"
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter the code we emailed you"
            required
            autoComplete="one-time-code"
          />
          <BrandedButton type="submit" variant="primary" fullWidth disabled={submitting}>
            {submitting ? 'Verifying...' : 'Verify code'}
          </BrandedButton>
          {email && (
            <BrandedButton
              type="button"
              variant="outline"
              fullWidth
              disabled={submitting}
              onClick={handleResend}
            >
              Send a new code
            </BrandedButton>
          )}
        </form>
      ) : (
        <form onSubmit={handleSubmitEmail} className="verification-form">
          <BrandedInput
            type="email"
            name="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            required
            autoComplete="email"
          />
          <BrandedButton type="submit" variant="primary" fullWidth disabled={submitting}>
            {submitting ? 'Sending...' : 'Send verification code'}
          </BrandedButton>
        </form>
      )}

      <div className="verification-links">
        <a href={signInPath(loginChallenge)} className="verification-link">
          Back to sign in
        </a>
      </div>
    </BrandedCard>
  );
};
